<template>
  <div class="ui-tabs">
    <div class="ui-th">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        :class="['ui-th-item', { active: activeTab === item.id }]"
        @click="() => setTab(index)"
      >
        <slot :name="`${item.id}Header`" :item="item" :active="activeTab === item.id">
          {{ item.label }}
        </slot>
        <div
          v-if="activeTab === item.id"
          class="absolute -bottom-px h-[3px] w-full rounded-t-md bg-[--c-primary-500] -mx-4"
        ></div>
      </div>
    </div>
    <template v-if="!hideContent">
      <div v-for="item in items" :key="item.id" class="">
        <div v-show="activeTab === item.id" v-motion-slide-top>
          <slot :name="item.id" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  items?: {
    id: string
    label: string
    disabled?: boolean
  }[]
  hideContent?: boolean
}>()

const modelValue = defineModel<string>()

const activeIndex = ref(0)
const activeTab = computed(() => modelValue.value || props.items?.[activeIndex.value]?.id)

const setTab = (tab: string | number, shouldEmit = true) => {
  const items = props.items || []
  if (!items.length) return

  let idx = 0
  if (typeof tab === 'number') {
    idx = tab
  } else {
    const foundIdx = items.findIndex((item) => item.id === tab)
    idx = foundIdx !== -1 ? foundIdx : 0
  }

  if (idx < 0 || idx >= items.length) idx = 0

  activeIndex.value = idx

  const nextTabId = items[idx]?.id
  if (shouldEmit && nextTabId && nextTabId !== modelValue.value) {
    modelValue.value = nextTabId
  }
}

watch(
  () => modelValue.value,
  (value) => {
    if (typeof value !== 'undefined') setTab(value, false)
  },
  { immediate: true }
)

defineExpose({
  setTab
})
</script>

<style>
.ui-th {
  @apply flex;

  .ui-th-item {
    @apply flex items-center relative text-sm leading-4 cursor-pointer px-4 py-3 border-b border-neutral-300 transition-all;

    &.active {
      @apply font-bold rounded-t-lg;
    }
  }
}
</style>
